import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BarChart3, TrendingUp, Clock, Target, Award } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { SEOMetaTags } from '../../components/shared/SEOMetaTags';
import { getStudentProfileByToken } from '../../services/elearningStudentService';
import { useTenantContext } from '../../context/TenantContext';
import StudentLayout from '../../components/student/StudentLayout';
import LearningStatsChart from '../../components/student/dashboard/LearningStatsChart';

const StudentProgressPage: React.FC = () => {
  const navigate = useNavigate();
  const { tenant } = useTenantContext();
  
  const [studentProfile, setStudentProfile] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Callback appelé quand le profil étudiant est chargé dans le layout
  const handleStudentProfileLoaded = (profile: any) => {
    setStudentProfile(profile);
    setIsLoading(false);
  };





  return (
    <StudentLayout
      title="Ma Progression"
      description="Suivez vos performances et votre évolution d'apprentissage"
      onStudentProfileLoaded={handleStudentProfileLoaded}
    >
      <SEOMetaTags 
        title="Ma Progression - E-Learning"
        description="Suivez votre progression d'apprentissage et vos statistiques."
        keywords="progression, statistiques, apprentissage, e-learning"
      />

      {/* Statistiques de progression */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Temps d'étude</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">24h</p>
                    <p className="text-xs text-green-500">+2h cette semaine</p>
                  </div>
                  <Clock className="h-8 w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Progression moy.</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">78%</p>
                    <p className="text-xs text-green-500">+5% ce mois</p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Objectifs atteints</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">5/7</p>
                    <p className="text-xs text-orange-500">2 en cours</p>
                  </div>
                  <Target className="h-8 w-8 text-orange-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Badges obtenus</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">12</p>
                    <p className="text-xs text-purple-500">3 ce mois</p>
                  </div>
                  <Award className="h-8 w-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>
      </div>

      {/* Graphique de progression */}
      <Card className="border-0 shadow-lg mb-8">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <BarChart3 className="h-5 w-5 text-purple-500" />
            <span>Évolution de votre apprentissage</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <LearningStatsChart 
            studentId={studentProfile?.id || ""}
            dateRange="month"
          />
        </CardContent>
      </Card>

      {/* Progression par cours */}
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle>Progression par cours</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Aucune donnée de progression disponible pour le moment.</p>
              <p className="text-sm mt-2">Commencez un cours pour voir vos statistiques ici.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </StudentLayout>
  );
};

export default StudentProgressPage;
